import { useEffect, useMemo } from "react";
import { inr } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const FONT_MAP = {
  Serif: '"Cormorant Garamond", serif',
  Script: '"Pinyon Script", cursive',
  Mono: '"JetBrains Mono", monospace',
};

// value: { selections: {optId: string}, font: string }
export default function Personalisation({ config, value, onChange }) {
  const selections = value?.selections || {};
  const font = value?.font || "Serif";

  const surcharge = useMemo(
    () => (config.options || []).reduce(
      (s, o) => s + (selections[o.id] ? Number(o.surcharge || 0) : 0), 0
    ),
    [config, selections]
  );

  const summary = useMemo(() => {
    const parts = [];
    for (const o of config.options || []) {
      const sel = selections[o.id];
      if (!sel) continue;
      if (o.type === "engraving") parts.push(`Engraving "${sel}"${o.fonts?.length ? ` (${font})` : ""}`);
      else if (o.type === "birthstone") parts.push(`Birthstone: ${sel}`);
      else if (o.type === "date") parts.push(`Date: ${sel}`);
      else if (o.type === "message") parts.push(`Back message: "${sel}"`);
    }
    return parts.join(" · ");
  }, [config, selections, font]);

  // propagate summary + surcharge upward (after render, not during)
  useEffect(() => {
    onChange({ selections, font, summary, surcharge });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, surcharge, font]);

  const setSel = (id, v) => onChange({ selections: { ...selections, [id]: v }, font, summary, surcharge });

  return (
    <div className="border border-brand/40 bg-brand-light/50 p-4 space-y-4" data-testid="personalisation-panel">
      <p className="eyebrow !text-brand">MAKE IT YOURS</p>
      {(config.options || []).map((opt) => (
        <div key={opt.id}>
          <Label className="text-sm">
            {opt.label}
            {opt.surcharge > 0 && <span className="text-stone-500 font-normal"> (+{inr(opt.surcharge)})</span>}
          </Label>
          {opt.type === "engraving" && (
            <div className="flex gap-2 mt-1.5">
              <Input
                value={selections[opt.id] || ""}
                maxLength={opt.char_limit || 12}
                onChange={(e) => setSel(opt.id, e.target.value)}
                placeholder={`Up to ${opt.char_limit || 12} characters`}
                className="bg-white"
                data-testid="pers-engraving-input"
              />
              {opt.fonts?.length > 0 && (
                <Select value={font} onValueChange={(v) => onChange({ selections, font: v, summary, surcharge })}>
                  <SelectTrigger className="w-28 bg-white" data-testid="pers-font-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {opt.fonts.map((f) => <SelectItem key={f} value={f} data-testid={`pers-font-${f.toLowerCase()}`}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}
          {opt.type === "birthstone" && (
            <Select value={selections[opt.id] || ""} onValueChange={(v) => setSel(opt.id, v)}>
              <SelectTrigger className="mt-1.5 bg-white" data-testid="pers-birthstone-select"><SelectValue placeholder="Choose birthstone" /></SelectTrigger>
              <SelectContent>
                {(opt.choices || []).map((c) => <SelectItem key={c} value={c} data-testid={`pers-birthstone-${c.toLowerCase()}`}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {opt.type === "date" && (
            <Input type="date" className="mt-1.5 bg-white" value={selections[opt.id] || ""} onChange={(e) => setSel(opt.id, e.target.value)} data-testid="pers-date-input" />
          )}
          {opt.type === "message" && (
            <div className="mt-1.5">
              <textarea
                value={selections[opt.id] || ""}
                maxLength={opt.char_limit || 40}
                onChange={(e) => setSel(opt.id, e.target.value)}
                placeholder={`A short message on the back (max ${opt.char_limit || 40} chars)`}
                className="w-full h-16 border border-stone-200 bg-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-stone-900"
                data-testid="pers-message-input"
              />
              <p className="text-[10px] font-mono text-stone-400 text-right">{(selections[opt.id] || "").length}/{opt.char_limit || 40}</p>
            </div>
          )}
        </div>
      ))}
      {summary && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="pers-nonreturnable-note">
          Personalised pieces are crafted to order and are <strong>non-returnable</strong>.
        </p>
      )}
    </div>
  );
}
